import { useEffect, useState } from 'react';
import { useMarkets } from '@/state/markets';
import { getTicker } from '@/content/tickers';
import { priceAt } from '@/engine/prices';
import { priceFmt, money, pct } from '@/lib/format';
import { cn } from '@/lib/cn';
import { sounds } from '@/audio/synth';

export function OpenOrders() {
  const orders = useMarkets((s) => s.openOrders);
  const positions = useMarkets((s) => s.positions);
  const cancelRaw = useMarkets((s) => s.cancelOrder);
  const cancel = (id: string) => { sounds.cancel(); cancelRaw(id); };
  const applyFill = useMarkets((s) => s.applyFill);
  const fills = useMarkets((s) => s.recentFills);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, []);

  const closePosition = (ticker: string, qty: number) => {
    const cfg = getTicker(ticker);
    if (!cfg) return;
    const p = priceAt(cfg, Date.now());
    applyFill(ticker, 'sell', qty, p);
    sounds.fill();
  };

  return (
    <div className="flex h-full flex-col gap-3 p-3 text-mono-xs">
      <Section title={`Positions`}>
        {positions.length === 0 ? (
          <Empty text="no positions" />
        ) : (
          positions.map((p) => {
            const cfg = getTicker(p.ticker);
            const livePrice = cfg ? priceAt(cfg, now) : 0;
            const pnl = (livePrice - p.avgPrice) * p.qty;
            const pnlPct = livePrice / p.avgPrice - 1;
            const positive = pnl >= 0;
            return (
              <Row key={p.ticker}>
                <span className="font-bold text-[var(--text-primary)]">{p.ticker}</span>
                <span className="tabular-nums">{p.qty.toFixed(0)}</span>
                <span className="tabular-nums text-[var(--text-secondary)]">@{priceFmt(p.avgPrice)}</span>
                <span className={cn('ml-auto tabular-nums', positive ? 'text-[var(--positive)]' : 'text-[var(--negative)]')}>
                  {money(pnl)} ({pct(pnlPct)})
                </span>
                <button
                  onClick={() => closePosition(p.ticker, p.qty)}
                  className="rounded border border-[var(--border-subtle)] px-1.5 py-0.5 text-[10px] uppercase text-[var(--text-secondary)] hover:bg-[var(--negative)] hover:text-black"
                  title="Close position at market"
                >
                  close
                </button>
              </Row>
            );
          })
        )}
      </Section>

      <Section title={`Open Orders`}>
        {orders.length === 0 ? (
          <Empty text="no open orders" />
        ) : (
          orders.map((o) => (
            <Row key={o.id}>
              <span className={cn('font-bold', o.side === 'buy' ? 'text-[var(--positive)]' : 'text-[var(--negative)]')}>
                {o.side.toUpperCase()}
              </span>
              <span>{o.ticker}</span>
              <span className="tabular-nums">{o.qty.toFixed(0)}</span>
              <span className="tabular-nums text-[var(--text-secondary)]">
                {o.type === 'limit'
                  ? `@${priceFmt(o.limitPrice ?? 0)}`
                  : o.type === 'stop'
                  ? `stop ${priceFmt(o.stopPrice ?? 0)}`
                  : o.type === 'trail'
                  ? `trail ${((o.trailPct ?? 0) * 100).toFixed(1)}%${o.trailPeak ? ` (peak ${priceFmt(o.trailPeak)})` : ''}`
                  : '@mkt'}
              </span>
              <button
                onClick={() => cancel(o.id)}
                className="ml-auto rounded px-1.5 py-0.5 text-[var(--text-muted)] hover:bg-[var(--bg-elevated)] hover:text-[var(--negative)]"
                aria-label="Cancel order"
              >
                ✕
              </button>
            </Row>
          ))
        )}
      </Section>

      <Section title="Recent Fills">
        {fills.length === 0 ? (
          <Empty text="no fills yet" />
        ) : (
          fills.slice(0, 10).map((f, i) => (
            <Row key={i}>
              <span className={cn('font-bold', f.side === 'buy' ? 'text-[var(--positive)]' : 'text-[var(--negative)]')}>
                {f.side.toUpperCase()}
              </span>
              <span>{f.ticker}</span>
              <span className="tabular-nums">{f.qty.toFixed(0)}</span>
              <span className="tabular-nums text-[var(--text-secondary)]">@{priceFmt(f.price)}</span>
              <span className="ml-auto tabular-nums text-[var(--text-muted)]">{money(f.qty * f.price)}</span>
            </Row>
          ))
        )}
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <div className="mb-1 text-[10px] uppercase tracking-wider text-[var(--text-muted)]">{title}</div>
      <div className="flex flex-col gap-0.5">{children}</div>
    </section>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 rounded px-1 py-0.5 hover:bg-[var(--bg-elevated)]">
      {children}
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return <div className="px-1 py-1 text-[var(--text-muted)] italic">{text}</div>;
}
